import {
    Input,
    Navbar,
    NavbarBrand,
    NavbarContent,
    NavbarItem,
    Image,
    Tabs, Tab,
    Avatar,
    Dropdown,
    DropdownTrigger,
    DropdownMenu,
    DropdownItem,
    Checkbox,
    Select,
    SelectItem,
    Button,
    ButtonGroup,
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Table,
    TableHeader,
    TableColumn,
    TableBody,
    TableRow,
    TableCell,
    Card,
    CardBody,
} from "@heroui/react";
import { RSKLogo } from "../../components/NavBar";
import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Icon } from "@iconify/react";
import { useTheme } from "@heroui/use-theme";
import ClaimsTable from "./ClaimsTable";
import BudgetTable from "./BudgetTable";
import { apiClientExporter } from "../../services/apiClient";
import { toast } from "sonner";
import { format } from "date-fns";


// Search Icon Component
const SearchIcon = (props) => (
    <svg
        aria-hidden="true"
        fill="none"
        focusable="false"
        height="1em"
        role="presentation"
        viewBox="0 0 24 24"
        width="1em"
        {...props}
    >
        <path
            d="M11.5 21C16.7467 21 21 16.7467 21 11.5C21 6.25329 16.7467 2 11.5 2C6.25329 2 2 6.25329 2 11.5C2 16.7467 6.25329 21 11.5 21Z"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
        />
        <path
            d="M22 22L20 20"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
        />
    </svg>
);

function ExporterContent({ data, quartersData: initialQuartersData, onLogout, profileData }) {
    const { theme, setTheme } = useTheme();
    
    // Estados para datos
    const [tableData, setTableData] = useState(data || []);
    const [quartersData, setQuartersData] = useState(initialQuartersData || []);
    const [profile] = useState(profileData || null);
    const [isLoadingCases, setIsLoadingCases] = useState(false);
    const [isLoadingQuarters, setIsLoadingQuarters] = useState(false);
    const [filteredQuartersCount, setFilteredQuartersCount] = useState(0);
    const [filteredCasesCount, setFilteredCasesCount] = useState(0);
    
    // Estados para tabs y proyecciones
    // Estados para tabs y proyecciones
    const [selectedView, setSelectedView] = useState("cases");
    const [selectedProjection, setSelectedProjection] = useState(1); // Default to QuickView id = 1
    const [isProjectionsMaintenanceModalOpen, setIsProjectionsMaintenanceModalOpen] = useState(false);
    
    // Normalizar quartersData para extraer siempre el array de quarters
    // quartersData ahora es un objeto con { quarters: [...], projections_names: [...] }
    const normalizedQuartersData = useMemo(() => {
        if (!quartersData) return [];
        
        // Si es un objeto con propiedad quarters, usar esa
        if (quartersData.quarters && Array.isArray(quartersData.quarters)) {
            return quartersData.quarters;
        }
        
        // Fallback: si es un array directo (compatibilidad con formato antiguo)
        if (Array.isArray(quartersData)) {
            return quartersData;
        }
        
        return [];
    }, [quartersData]);

    // Asegurar que solo se muestre Cases si no hay datos de quarters
    const hasQuartersData = normalizedQuartersData && normalizedQuartersData.length > 0;
    
    // Check if user has jefejudicial permission
    const hasJefeJudicialPermission = useMemo(() => {
        if (!profile || !profile.permissions) return false;
        return profile.permissions.includes('jefejudicial');
    }, [profile]);

    const hasClaimsReadPermission = useMemo(() => {
        if (!profile || !profile.permissions) return false;
        return profile.permissions.includes('claimsread');
    }, [profile]);

    // Always show tabs if user has admin permissions (profile_id 2, 5, 6)
    const shouldShowTabs = profile && (profile.profile_id === 2 || profile.profile_id === 5 || profile.profile_id === 6);

    // Funciones para cargar datos
    const loadCasesData = async () => {
        setIsLoadingCases(true);
        try {
            const token = localStorage.getItem("exporterAuthToken");
            const response = await apiClientExporter.get(`/exporter/claims`, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                },
            });
            setTableData(response.data);
            console.log("Cases data reloaded:", response.data);
        } catch (error) {
            console.error("Error loading cases data:", error);
        } finally {
            setIsLoadingCases(false);
        }
    };

    // Función para actualizar solo un caso específico por rsk_ref
    const updateSingleCase = async (rskRef) => {
        try {
            const token = localStorage.getItem("exporterAuthToken");
            const response = await apiClientExporter.get(`/exporter/claim?rsk_ref=${rskRef}`, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                },
            });
            
            // Actualizar solo esa fila en tableData
            setTableData(prevData => {
                if (!prevData || !Array.isArray(prevData)) return prevData;
                return prevData.map(row => {
                    // Comparar rsk_ref normalizando (puede tener / o -)
                    const rowRskRef = row.rsk_ref?.replace(/\//g, '-') || '';
                    const searchRskRef = rskRef.replace(/\//g, '-');
                    if (rowRskRef === searchRskRef) {
                        return response.data;
                    }
                    return row;
                });
            });
        } catch (error) {
            console.error("Error updating single case:", error);
            // Si falla, recargar todos los datos como fallback
            loadCasesData();
        }
    };

    const loadQuartersData = async () => {
        setIsLoadingQuarters(true);
        try {
            const token = localStorage.getItem("exporterAuthToken");
            const response = await apiClientExporter.get(`/exporter/claims_quarter`, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                },
            });
            setQuartersData(response.data);
            console.log("Quarters data reloaded:", response.data);
        } catch (error) {
            console.error("Error loading quarters data:", error);
        } finally {
            setIsLoadingQuarters(false);
        }
    };

    // Función para actualizar solo un quarter específico en el estado local (actualización optimista)
    const updateQuarterInState = (quarterId, updateFn) => {
        setQuartersData(prevData => {
            if (!prevData) return prevData;
            
            // Si es un objeto con quarters
            if (prevData.quarters && Array.isArray(prevData.quarters)) {
                return {
                    ...prevData,
                    quarters: prevData.quarters.map(q => 
                        q.id === quarterId ? updateFn(q) : q
                    )
                };
            }
            
            // Si es un array directo
            if (Array.isArray(prevData)) {
                return prevData.map(q => 
                    q.id === quarterId ? updateFn(q) : q
                );
            }
            
            return prevData;
        });
    };

    // Recargar datos cuando se cambia de pestaña
    useEffect(() => {
        // Si no tiene permiso jefejudicial y está en quarters, redirigir a cases
        if (selectedView === "quarters" && !hasJefeJudicialPermission) {
            setSelectedView("cases");
            return;
        }
        
        if (selectedView === "cases") {
            loadCasesData();
        } else if (selectedView === "quarters") {
            loadQuartersData();
        }
    }, [selectedView, hasJefeJudicialPermission]);

    // Función para recargar proyecciones después de cambios en mantenimiento
    const reloadProjections = async () => {
        await loadQuartersData();
    };

    const [casesFilterValue, setCasesFilterValue] = useState("");
    const [quartersFilterValue, setQuartersFilterValue] = useState("");
    const [myCasesOnly, setMyCasesOnly] = useState(false);
    const [selectedACargo, setSelectedACargo] = useState("all"); // Filtro por A Cargo, "all" por defecto
    const [filterMode, setFilterMode] = useState("OR"); // "OR" para búsqueda, "AND" para filtro
    // Pagination and sorting states
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(15);
    const [sortDescriptor, setSortDescriptor] = useState({
        column: "rsk_ref",
        direction: "descending",
    });

    // Extraer proyecciones únicas de projections_names
    // quartersData ahora es un objeto con { quarters: [...], projections_names: [...] }
    const uniqueProjections = useMemo(() => {
        // Acceder a projections_names desde quartersData (objeto con quarters y projections_names)
        const projections_names = quartersData?.projections_names;
            
        // Start with "All" option
        const projections = [{ id: 0, title: "All" }];
        
        if (!projections_names || !Array.isArray(projections_names)) {
            // If no projections_names, add QuickView as default
            projections.push({ id: 1, title: "QuickView" });
            return projections;
        }
        
        // Add all valid projections and sort by id (excluding "All" which is already first)
        const validProjections = projections_names
            .filter(proj => proj.id && proj.title) // Filter out any invalid projections
            .map(proj => ({ id: proj.id, title: proj.title }))
            .sort((a, b) => a.id - b.id); // Sort by id
        
        projections.push(...validProjections);
        
        return projections;
    }, [quartersData?.projections_names]);

    // Obtener valores únicos de A Cargo para el selector
    const uniqueACargoValues = useMemo(() => {
        const aCargoSet = new Set();
        if (tableData && Array.isArray(tableData)) {
            tableData.forEach(row => {
                if (row?.a_cargo && row.a_cargo.trim() !== '') {
                    aCargoSet.add(row.a_cargo);
                }
            });
        }
        if (normalizedQuartersData && Array.isArray(normalizedQuartersData)) {
            normalizedQuartersData.forEach(row => {
                if (row?.a_cargo && row.a_cargo.trim() !== '') {
                    aCargoSet.add(row.a_cargo);
                }
            });
        }
        return Array.from(aCargoSet).sort();
    }, [tableData, normalizedQuartersData]);

    // Toggle between light and dark theme
    const toggleTheme = () => {
        setTheme(theme === "light" ? "dark" : "light");
    };

    // Handle logout
    const handleLogout = () => {
        localStorage.removeItem("exporterAuthToken");
        if (onLogout) onLogout();
    };

    // Calculate filtered cases count and total claim amount from filtered table data
    const filteredCasesData = useMemo(() => {
        if (!tableData || !Array.isArray(tableData)) return [];
        
        let filtered = [...tableData]; // Crear copia para evitar mutaciones
        
        // Apply "My Cases" filter if enabled
        if (myCasesOnly) {
            filtered = filtered.filter((row) => {
                if (!row) return false;
                // For admin users, show all cases
                if (profile?.profile_id === 2 || profile?.profile_id === 5 || profile?.profile_id === 6) {
                    return true;
                }
                // For non-admin users, show only their cases
                return row.user_id === profile?.id;
            });
        }
        
        // Apply search filter with support for multiple criteria separated by comma
        if (casesFilterValue && casesFilterValue.trim().length > 0) {
            // Separar múltiples criterios por coma y limpiar espacios
            const searchTerms = casesFilterValue
                .split(',')
                .map(term => term.trim())
                .filter(term => term.length > 0);
            
            // Solo aplicar filtro si hay al menos un término válido
            if (searchTerms.length > 0) {
                filtered = filtered.filter((row) => {
                    if (!row) return false;

                    // Función helper para verificar si un término coincide con la fila
                    const termMatches = (searchTerm) => {
                        const lowerCaseFilter = searchTerm.toLowerCase();
                        const normalizedFilter = lowerCaseFilter.replace(/-/g, "/");

                        // Comparación especial para rsk_ref permitiendo '-' como '/'
                        const rsk = (row.rsk_ref || "").toString().toLowerCase();
                        const rskNormalized = rsk.replace(/-/g, "/");
                        const matchesRsk = rsk.includes(normalizedFilter) || rskNormalized.includes(normalizedFilter);

                        if (matchesRsk) return true;

                        // Comparación estándar para el resto de campos
                        const searchFields = [
                            row.status,
                            row.vessel,
                            row.bl_awb,
                            row.container_number,
                            row.carrier_name,
                            row.claimamount?.toString(),
                            row.exporter_name || "",
                            row.ofertas_de_cierre?.toString(),
                            row.recuperado_usd?.toString(),
                            row.fecha_finiquito ? format(new Date(row.fecha_finiquito), "dd/MM/yyyy") : "",
                        ];
                        
                        return searchFields.some((value) => {
                            if (value === null || value === undefined || value === "") return false;
                            try {
                                return String(value).toLowerCase().includes(lowerCaseFilter);
                            } catch (e) {
                                return false;
                            }
                        });
                    };

                    // Modo OR: si alguno de los términos coincide
                    // Modo AND: todos los términos deben coincidir
                    if (filterMode === "AND") {
                        return searchTerms.every(term => termMatches(term));
                    } else {
                        return searchTerms.some(term => termMatches(term));
                    }
                });
            }
        }
        
        return filtered;
    }, [tableData, myCasesOnly, casesFilterValue, profile, filterMode]);

    // Update filtered cases count
    useEffect(() => {
        setFilteredCasesCount(filteredCasesData.length);
    }, [filteredCasesData.length]);

    // Calculate total claim amount from filtered data
    const totalClaimAmount = useMemo(() => {
        return filteredCasesData.reduce((sum, row) => {
            const amount = row.claimamount;
            // Convertir a número si es string, manejar null/undefined
            const numAmount = typeof amount === 'string' 
                ? parseFloat(amount.replace(/[^0-9.-]/g, '')) || 0
                : (typeof amount === 'number' ? amount : 0);
            return sum + numAmount;
        }, 0);
    }, [filteredCasesData]);

    // Calculate total recovered amount from filtered data
    const totalRecoveredAmount = useMemo(() => {
        return filteredCasesData.reduce((sum, row) => {
            const amount = row.recuperado_usd;
            // Convertir a número si es string, manejar null/undefined
            const numAmount = typeof amount === 'string' 
                ? parseFloat(amount.replace(/[^0-9.-]/g, '')) || 0
                : (typeof amount === 'number' ? amount : 0);
            return sum + numAmount;
        }, 0);
    }, [filteredCasesData]);


    // Memoized search input handlers
    const onCasesSearchChange = useCallback((value) => {
        if (value) {
            setCasesFilterValue(value);
            setPage(1);
        } else {
            setCasesFilterValue("");
        }
    }, []);

    const onCasesSearchClear = useCallback(() => {
        setCasesFilterValue("");
        setPage(1);
    }, []);

    const onQuartersSearchChange = useCallback((value) => {
        if (value) {
            setQuartersFilterValue(value);
            setPage(1);
        } else {
            setQuartersFilterValue("");
        }
    }, []);

    const onQuartersSearchClear = useCallback(() => {
        setQuartersFilterValue("");
        setPage(1);
    }, []);

    // Define the top content for search and pagination
    const topContent = useMemo(() => {
        return (
            <div className="flex flex-col gap-4">
                <div className="flex justify-between gap-3 items-end">
                    <div className="flex gap-3 items-center">
                        <Input
                            isClearable
                            className="flex-1"
                            placeholder="Search by RSK Ref, BL/AWB, Exporter, etc."
                            startContent={<SearchIcon />}
                            value={selectedView === "cases" || !hasQuartersData ? casesFilterValue : quartersFilterValue}
                            onClear={selectedView === "cases" || !hasQuartersData ? onCasesSearchClear : onQuartersSearchClear}
                            onValueChange={selectedView === "cases" || !hasQuartersData ? onCasesSearchChange : onQuartersSearchChange}
                            variant="bordered"
                            radius="md"
                            color="primary"
                        />
                        {(selectedView === "cases" || !hasQuartersData) && (
                            <ButtonGroup
                                size="sm"
                                variant="bordered"
                                className="w-auto"
                                aria-label="Filter mode"
                                isDisabled={false}
                            >
                                <Button
                                    onPress={() => {
                                        setFilterMode("OR");
                                    }}
                                    color={filterMode === "OR" ? "primary" : "default"}
                                    className={filterMode === "OR" ? "font-semibold" : ""}
                                    isDisabled={false}
                                >
                                    Search (OR)
                                </Button>
                                <Button
                                    onPress={() => {
                                        setFilterMode("AND");
                                    }}
                                    color={filterMode === "AND" ? "primary" : "default"}
                                    className={filterMode === "AND" ? "font-semibold" : ""}
                                    isDisabled={false}
                                >
                                    Filter (AND)
                                </Button>
                            </ButtonGroup>
                        )}
                        <Checkbox
                            isSelected={myCasesOnly}
                            onValueChange={setMyCasesOnly}
                            color="primary"
                            size="sm"
                        >
                            My Claims
                        </Checkbox>
                        {hasJefeJudicialPermission && uniqueACargoValues.length > 0 && (
                            <Select
                                key={`a-cargo-select-${selectedACargo}`}
                                placeholder="A Cargo"
                                selectedKeys={new Set([selectedACargo])}
                                onSelectionChange={(keys) => {
                                    const selected = Array.from(keys)[0];
                                    if (selected) {
                                        setSelectedACargo(selected);
                                        setPage(1); // Reset page when filter changes
                                    }
                                }}
                                className="w-32"
                                size="sm"
                                variant="bordered"
                                radius="md"
                                classNames={{
                                    trigger: "h-9 min-h-9",
                                    popoverContent: "bg-white dark:bg-gray-900 border border-default-200",
                                    listbox: "p-0",
                                }}
                            >
                                <SelectItem
                                    key="all"
                                    className="text-sm data-[hover=true]:bg-default-100 data-[selectable=true]:data-[pressed=true]:bg-default-200"
                                >
                                    All Users
                                </SelectItem>
                                {uniqueACargoValues.map((aCargo) => (
                                    <SelectItem
                                        key={aCargo}
                                        className="text-sm data-[hover=true]:bg-default-100 data-[selectable=true]:data-[pressed=true]:bg-default-200"
                                    >
                                        {aCargo}
                                    </SelectItem>
                                ))}
                            </Select>
                        )}
                        {selectedView === "quarters" && (
                            <div className="flex items-center gap-2">
                                <span className="text-default-400 text-small">Projection:</span>
                                <Select
                                    size="sm"
                                    variant="bordered"
                                    placeholder="Select projection"
                                    selectedKeys={[selectedProjection.toString()]}
                                    onSelectionChange={(keys) => {
                                        const selected = Array.from(keys)[0];
                                        if (selected) setSelectedProjection(parseInt(selected));
                                    }}
                                    className="w-40"
                                    classNames={{
                                        trigger: "h-9 min-h-9",
                                        popoverContent: "bg-white dark:bg-gray-900 border border-default-200",
                                        listbox: "p-0",
                                    }}
                                >
                                    {uniqueProjections.map((projection) => (
                                        <SelectItem
                                            key={projection.id.toString()}
                                            value={projection.id.toString()}
                                            className="text-sm data-[hover=true]:bg-default-100 data-[selectable=true]:data-[pressed=true]:bg-default-200"
                                        >
                                            {projection.title}
                                        </SelectItem>
                                    ))}
                                </Select>
                                <Button
                                    size="sm"
                                    variant="flat"
                                    color="secondary"
                                    isIconOnly
                                    onPress={() => setIsProjectionsMaintenanceModalOpen(true)}
                                    title="Manage Projections"
                                >
                                    <Icon icon="mdi:cog" width={18} height={18} />
                                </Button>
                            </div>
                        )}
                    </div>
                    {shouldShowTabs && (
                        <Tabs
                            aria-label="View Tabs"
                            selectedKey={selectedView}
                            onSelectionChange={setSelectedView}
                        >
                            <Tab key="cases" title="Claims" />
                            {hasJefeJudicialPermission && (
                                <Tab key="quarters" title="Budget" />
                            )}
                        </Tabs>
                    )}
                </div>
                <div className="flex justify-between items-center">
                    <span className="text-default-400 text-small">
                        Total {selectedView === "cases" || !shouldShowTabs ? filteredCasesCount : filteredQuartersCount} {selectedView === "cases" || !shouldShowTabs ? "claims" : "registries"}
                        {selectedView === "cases" || !shouldShowTabs ? (
                            <>
                                <span className="ml-2 text-default-600 font-medium">
                                    | Total Claim Amount: USD${new Intl.NumberFormat("es-CL", {
                                        minimumFractionDigits: 0, //2
                                        maximumFractionDigits: 0, //2
                                    }).format(totalClaimAmount)}
                                </span>
                                <span className="ml-2 text-default-600 font-medium">
                                    | Total Recovered Amount: USD${new Intl.NumberFormat("es-CL", {
                                        minimumFractionDigits: 0, //2
                                        maximumFractionDigits: 0, //2
                                    }).format(totalRecoveredAmount)}
                                </span>
                            </>
                        ) : null}
                </span>
                    <label className="flex items-center text-default-400 text-small">
                        Rows per page:
                        <select
                            className="bg-transparent outline-none text-default-400 text-small ml-2"
                            value={rowsPerPage}
                            onChange={(e) => {
                                const newRows = e.target.value === "all" ? (selectedView === "cases" || !hasQuartersData ? tableData?.length || 0 : normalizedQuartersData?.length || 0) : Number(e.target.value);
                                setRowsPerPage(newRows);
                                setPage(1);
                            }}
                        >
                            <option value="10">10</option>
                            <option value="15">15</option>
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                            <option value="200">200</option>
                            <option value="all">All</option>
                        </select>
                    </label>
                </div>
            </div>
        );
    }, [
        casesFilterValue,
        quartersFilterValue,
        tableData,
        normalizedQuartersData,
        onCasesSearchChange,
        onCasesSearchClear,
        onQuartersSearchChange,
        onQuartersSearchClear,
        rowsPerPage,
        myCasesOnly,
        selectedView,
        selectedProjection,
        uniqueProjections,
        shouldShowTabs,
        totalClaimAmount,
        totalRecoveredAmount,
        hasQuartersData,
        filteredCasesCount,
        filteredQuartersCount,
        filterMode,
        selectedACargo,
        hasJefeJudicialPermission,
        uniqueACargoValues
    ]);

    // Bottom content for pagination
    const bottomContent = useMemo(() => {
        const totalItems = selectedView === "cases" || !shouldShowTabs ? filteredCasesCount : filteredQuartersCount;
        const pages = Math.ceil(totalItems / rowsPerPage) || 1;
        
        return (
            <div className="py-2 px-2 flex justify-between items-center">
                <div className="text-small text-default-400">
                    Showing {Math.min((page - 1) * rowsPerPage + 1, totalItems)} to {Math.min(page * rowsPerPage, totalItems)} of {totalItems} entries
                </div>
                <div className="flex gap-2">
                    <button
                        disabled={page <= 1}
                        onClick={() => setPage(prev => Math.max(1, prev - 1))}
                        className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Previous
                    </button>
                    <span className="px-3 py-1 text-sm">
                        Page {page} of {pages}
                    </span>
                    <button
                        disabled={page >= pages}
                        onClick={() => setPage(prev => Math.min(pages, prev + 1))}
                        className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Next
                    </button>
                </div>
            </div>
        );
    }, [page, rowsPerPage, selectedView, filteredCasesCount, filteredQuartersCount, shouldShowTabs]);

// Validate profile and tableData before rendering
    if (!profile || !tableData) {
        return <div>Loading...</div>;
    }

    return (
        <>
            <Navbar className="mb-2">
                <NavbarBrand>
                    <div className="flex gap-1 justify-center items-center" >
                        <RSKLogo />
                        <Image
                            src= {process.env.PUBLIC_URL + "/PoweredBy.png"}
                            className="max-w-[55px] max-h-[30px] object-contain"
                            width={55}
                            height={30}
                            alt="AWS Logo"
                        />
                    </div>
                </NavbarBrand>
                <NavbarContent className="hidden sm:flex gap-4" justify="center">
                    <NavbarItem>
                        <span className="text-default-600 font-medium">{profile.company_name ?? "RSK Quickview"}</span>
                    </NavbarItem>
                </NavbarContent>
                <NavbarContent justify="end">
                    {profile?.logo && (
                        <NavbarItem>
                            <Image
                                src={profile.logo}
                                alt="Exporter Logo"
                                className="max-w-[70px] max-h-[40px] object-contain"
                                width={70}
                                height={40}
                            />
                        </NavbarItem>
                    )}

                    <NavbarItem>
                        <button
                            onClick={toggleTheme}
                            aria-label="Toggle Theme"
                            className="transition-all duration-300 hover:scale-110 p-2 rounded-lg hover:bg-default-100"
                        >
                            <Icon
                                icon={theme === "light" ? "solar:moon-stars-bold-duotone" : "solar:sun-2-bold-duotone"}
                                width={28}
                                height={28}
                                className={theme === "light" ? "text-blue-700" : "text-yellow-400"}
                            />
                        </button>
                    </NavbarItem>
                    <NavbarItem>
                        <Dropdown placement="bottom-end">
                            <DropdownTrigger>
                                <Avatar
                                    isBordered
                                    as="button"
                                    className="transition-transform"
                                    color="secondary"
                                    name={
                                        profile?.first_name && profile?.last_name
                                            ? `${profile.first_name.charAt(0).toUpperCase()}${profile.last_name.charAt(0).toUpperCase()}`
                                            : "N/A"
                                    }
                                    size="sm"
                                />
                            </DropdownTrigger>
                            <DropdownMenu aria-label="Profile Actions" variant="flat">
                                <DropdownItem key="profile" className="h-14 gap-2">
                                    <p className="font-semibold">
                                        {profile
                                            ? `Signed in as ${profile.first_name} ${profile.last_name}`
                                            : "Not signed in"}
                                    </p>
                                    <p className="font-semibold">
                                        {profile?.email || "no email available"}
                                    </p>
                                </DropdownItem>
                                <DropdownItem 
                                    key="logout" 
                                    color="danger" 
                                    onPress={handleLogout}
                                    startContent={<Icon icon="mdi:logout" width="16" height="16" />}
                                >
                                    Log Out
                                </DropdownItem>
                                {process.env.REACT_APP_VERSION && (
                                    <DropdownItem
                                        key="app-version"
                                        isDisabled
                                        className="text-xs text-default-400"
                                    >
                                        Version: {process.env.REACT_APP_VERSION}
                                    </DropdownItem>
                                )}
                            </DropdownMenu>
                        </Dropdown>
                    </NavbarItem>
                </NavbarContent>
            </Navbar>

            <div className="mx-auto w-full max-w-none px-2" >
                {selectedView === "cases" || !shouldShowTabs ? (
                    isLoadingCases ? (
                        <div className="flex justify-center items-center p-8">
                            <div className="text-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                                <p className="text-gray-600">Loading claims...</p>
                            </div>
                        </div>
                    ) : (
                        <ClaimsTable 
                            tableData={tableData}
                            profile={profile}
                            filterValue={casesFilterValue}
                            setFilterValue={setCasesFilterValue}
                            myCasesOnly={myCasesOnly}
                            setMyCasesOnly={setMyCasesOnly}
                            rowsPerPage={rowsPerPage}
                            setRowsPerPage={setRowsPerPage}
                            page={page}
                            setPage={setPage}
                            sortDescriptor={sortDescriptor}
                            setSortDescriptor={setSortDescriptor}
                            topContent={topContent}
                            bottomContent={bottomContent}
                            filterMode={filterMode}
                            onCasesDataReload={loadCasesData}
                            onUpdateSingleCase={updateSingleCase}
                            aCargoFilter={selectedACargo}
                            readOnlyClaims={hasClaimsReadPermission}
                            aCargoOptions={uniqueACargoValues}
                        />
                    )
                ) : (
                    isLoadingQuarters ? (
                        <div className="flex justify-center items-center p-8">
                                    <div className="text-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                                <p className="text-gray-600">Loading budget...</p>
                            </div>
                        </div>
                    ) : (
                        <BudgetTable 
                            quartersData={normalizedQuartersData}
                            selectedProjection={selectedProjection}
                            projectionsList={uniqueProjections}
                            profile={profile}
                            aCargoFilter={selectedACargo}
                            aCargoOptions={uniqueACargoValues}
                            myCasesOnly={myCasesOnly}
                            filterValue={quartersFilterValue}
                            setFilterValue={setQuartersFilterValue}
                            rowsPerPage={rowsPerPage}
                            setRowsPerPage={setRowsPerPage}
                            page={page}
                            setPage={setPage}
                            sortDescriptor={sortDescriptor}
                            setSortDescriptor={setSortDescriptor}
                            topContent={topContent}
                            bottomContent={bottomContent}
                            onFilteredDataChange={setFilteredQuartersCount}
                            onReloadData={loadQuartersData}
                            onUpdateQuarter={updateQuarterInState}
                        />
                    )
                )}
            </div>

            {/* Projections Maintenance Modal */}
            <ProjectionsMaintenanceModal
                isOpen={isProjectionsMaintenanceModalOpen}
                onClose={() => setIsProjectionsMaintenanceModalOpen(false)}
                onReload={reloadProjections}
            />
        </>
    )
}

// Componente modal para mantenimiento de proyecciones
function ProjectionsMaintenanceModal({ isOpen, onClose, onReload }) {
    const [projections, setProjections] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({ title: '', description: '' });
    const [isSaving, setIsSaving] = useState(false);

    // Cargar proyecciones cuando se abre el modal
    useEffect(() => {
        if (isOpen) {
            loadProjections();
        }
    }, [isOpen]);

    const loadProjections = async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem("exporterAuthToken");
            const response = await apiClientExporter.get('/exporter/projections/list', {
                headers: {
                    "Authorization": `Bearer ${token}`,
                },
            });
            setProjections(response.data);
        } catch (error) {
            console.error("Error loading projections:", error);
            toast.error("Error loading projections");
        } finally {
            setIsLoading(false);
        }
    };

    const handleAdd = () => {
        setIsEditing(true);
        setEditingId(null);
        setFormData({ title: '', description: '' });
    };

    const handleEdit = (projection) => {
        setIsEditing(true);
        setEditingId(projection.id);
        setFormData({ title: projection.title, description: projection.description || '' });
    };

    const handleCancel = () => {
        setIsEditing(false);
        setEditingId(null);
        setFormData({ title: '', description: '' });
    };

    const handleSave = async () => {
        if (!formData.title || formData.title.trim() === '') {
            toast.error('Title is required');
            return;
        }

        setIsSaving(true);
        try {
            const token = localStorage.getItem("exporterAuthToken");
            
            if (editingId) {
                // Actualizar
                await apiClientExporter.put(`/exporter/projections/update/${editingId}`, formData, {
                    headers: {
                        "Authorization": `Bearer ${token}`,
                    },
                });
                toast.success('Projection updated successfully');
            } else {
                // Crear
                await apiClientExporter.post('/exporter/projections/create', formData, {
                    headers: {
                        "Authorization": `Bearer ${token}`,
                    },
                });
                toast.success('Projection created successfully');
            }
            
            handleCancel();
            await loadProjections();
            if (onReload) {
                await onReload();
            }
        } catch (error) {
            console.error("Error saving projection:", error);
            const errorMessage = error.response?.data?.error || 'Error saving projection';
            toast.error(errorMessage);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this projection?')) {
            return;
        }

        try {
            const token = localStorage.getItem("exporterAuthToken");
            await apiClientExporter.delete(`/exporter/projections/delete/${id}`, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                },
            });
            toast.success('Projection deleted successfully');
            await loadProjections();
            if (onReload) {
                await onReload();
            }
        } catch (error) {
            console.error("Error deleting projection:", error);
            const errorMessage = error.response?.data?.error || 'Error deleting projection';
            toast.error(errorMessage);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="3xl"
            scrollBehavior="inside"
        >
            <ModalContent>
                <>
                    <ModalHeader>
                        <div className="flex flex-col gap-1">
                            <h3>Manage Projections</h3>
                            <p className="text-sm text-gray-500">
                                Add, edit, or delete projection types
                            </p>
                        </div>
                    </ModalHeader>
                    <ModalBody>
                        {isLoading ? (
                            <div className="flex justify-center items-center p-8">
                                <div className="text-center">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                                    <p className="text-gray-600">Loading projections...</p>
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col gap-4">
                                {!isEditing ? (
                                    <>
                                        <div className="flex justify-end">
                                            <Button
                                                color="primary"
                                                size="sm"
                                                onPress={handleAdd}
                                                startContent={<Icon icon="mdi:plus" width={18} height={18} />}
                                            >
                                                Add Projection
                                            </Button>
                                        </div>
                                        {projections.length === 0 ? (
                                            <p className="text-center text-gray-500 py-4">No projections found</p>
                                        ) : (
                                            <Table aria-label="Projections table">
                                                <TableHeader>
                                                    <TableColumn>ID</TableColumn>
                                                    <TableColumn>TITLE</TableColumn>
                                                    <TableColumn>DESCRIPTION</TableColumn>
                                                    <TableColumn>CREATED</TableColumn>
                                                    <TableColumn>ACTIONS</TableColumn>
                                                </TableHeader>
                                                <TableBody>
                                                    {projections.map((projection) => (
                                                        <TableRow key={projection.id}>
                                                            <TableCell>{projection.id}</TableCell>
                                                            <TableCell className="font-semibold">{projection.title}</TableCell>
                                                            <TableCell>{projection.description || '-'}</TableCell>
                                                            <TableCell>
                                                                {projection.created 
                                                                    ? new Date(projection.created).toLocaleDateString('es-CL')
                                                                    : '-'}
                                                            </TableCell>
                                                            <TableCell>
                                                                <div className="flex gap-2">
                                                                    <Button
                                                                        size="sm"
                                                                        variant="flat"
                                                                        color="primary"
                                                                        isIconOnly
                                                                        onPress={() => handleEdit(projection)}
                                                                        title="Edit"
                                                                    >
                                                                        <Icon icon="mdi:pencil" width={18} height={18} />
                                                                    </Button>
                                                                    <Button
                                                                        size="sm"
                                                                        variant="flat"
                                                                        color="danger"
                                                                        isIconOnly
                                                                        onPress={() => handleDelete(projection.id)}
                                                                        title="Delete"
                                                                    >
                                                                        <Icon icon="mdi:delete" width={18} height={18} />
                                                                    </Button>
                                                                </div>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        )}
                                    </>
                                ) : (
                                    <Card>
                                        <CardBody>
                                            <div className="flex flex-col gap-4">
                                                <h4 className="font-semibold">
                                                    {editingId ? 'Edit Projection' : 'Add New Projection'}
                                                </h4>
                                                <Input
                                                    label="Title"
                                                    placeholder="e.g., Revisión Agosto"
                                                    value={formData.title}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                                                    isRequired
                                                    isDisabled={isSaving}
                                                />
                                                <Input
                                                    label="Description"
                                                    placeholder="Optional description"
                                                    value={formData.description}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                                    isDisabled={isSaving}
                                                />
                                                <div className="flex gap-2 justify-end">
                                                    <Button
                                                        variant="light"
                                                        onPress={handleCancel}
                                                        isDisabled={isSaving}
                                                    >
                                                        Cancel
                                                    </Button>
                                                    <Button
                                                        color="primary"
                                                        onPress={handleSave}
                                                        isLoading={isSaving}
                                                        isDisabled={isSaving}
                                                    >
                                                        {isSaving ? 'Saving...' : 'Save'}
                                                    </Button>
                                                </div>
                                            </div>
                                        </CardBody>
                                    </Card>
                                )}
                            </div>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button color="danger" variant="light" onPress={onClose}>
                            Close
                        </Button>
                    </ModalFooter>
                </>
            </ModalContent>
        </Modal>
    );
}

export default ExporterContent;